import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import Footer from "../components/Footer.jsx";
import ProductCard from "../components/ProductCard.jsx";
import { useCart } from "../context/CartContext.jsx";
import { useMediaQuery } from "../hooks/useMediaQuery.js";
import { formatCurrency, formatDisplayPrice } from "../utils/currency.js";
import { TABLET_QUERY } from "../utils/viewport.js";
import { lockScroll, unlockScroll } from "../utils/scrollLock.js";
import { fetchShowcaseHiddenBrands, getShowcaseHiddenBrands } from "../utils/inventoryWarmup.js";
import klarnaBadge from "../assets/klarna_payment_badge.png";

// How far above the bottom of the first screen YOU MAY ALSO LIKE's caption
// sits on a desktop, so the section peeks up into view (relatedRef).
const RELATED_PEEK_PX = 100;

// The fullscreen viewer's zoom (.product-fullscreen__canvas--zoomed img in
// app.css). A touch drag moves the zoom origin against the finger by
// 1 / (ZOOM - 1) of the drag, which makes the photo follow the finger.
const FS_ZOOM = 2.5;
const clampPct = (v) => Math.max(0, Math.min(100, v));


export default function ProductPage() {
  const { itemId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // Where the back arrow returns to. Only the catalog (FeaturedPage), the
  // brand page (BrandCarousel) and the landing page's new-arrivals row tag
  // their item links with `state.from`, so items opened from the cart, a
  // pasted URL or a fresh tab get no arrow.
  const backTo = useMemo(() => {
    const from = location.state?.from;
    // No known origin (the cart, a pasted link, a fresh tab): back to the
    // catalog, so the arrow is always there (the owner, 2026-10-02).
    if (typeof from !== "string") return "/featured";
    // "/brand" also covers "/brands": older history entries can still carry
    // either, and both routes redirect to /showcase.
    if (from !== "/" && !from.startsWith("/featured") && !from.startsWith("/showcase") && !from.startsWith("/brand")) return "/featured";
    return from;
  }, [location.state]);
  // Back to where the piece was opened from, as an ordinary route change:
  // PageTransition's own handling, the same as any other link. It used to
  // fade the whole screen to black first (startNavFade, 700ms out, then the
  // reveal), which made the way back slow. The scroll resets at the swap, as
  // the header's section links do: the catalog is this page's own route
  // section, which PageTransition swaps instantly, so it resets now;
  // anywhere else this page fades out for 150ms first, and resetting sooner
  // would jolt it to its top mid-fade.
  const handleBack = useCallback(() => {
    if (!backTo) return;
    // Into the catalog, with its first-load entrance (FeaturedPage initialLoad).
    navigate(backTo, backTo.startsWith("/featured") ? { state: { catalogEntrance: true } } : undefined);
    if (backTo.startsWith("/featured")) window.scrollTo({ top: 0 });
    else setTimeout(() => window.scrollTo({ top: 0 }), 160);
  }, [backTo, navigate]);
  const { addItem, removeItem, isInCart } = useCart();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [selectedSize, setSelectedSize] = useState(null);
  const [addingToCart, setAddingToCart] = useState(false);
  const [addedToCart, setAddedToCart] = useState(false);
  const [relatedItems, setRelatedItems] = useState([]);
  // YOU MAY ALSO LIKE peeks up from the bottom of the first screen on a
  // desktop (the owner, 2026-10-02): its caption RELATED_PEEK_PX above the
  // screen's edge. The product block above does not grow with the screen,
  // so the gap is measured from it, at the page's top, on load and resize.
  const relatedRef = useRef(null);
  useEffect(() => {
    const el = relatedRef.current;
    if (!el) return undefined;
    const wide = window.matchMedia("(min-width: 1024px)");
    const place = () => {
      if (!wide.matches) { el.style.marginTop = ""; return; }
      el.style.marginTop = "0px";
      const top = el.getBoundingClientRect().top + window.scrollY;
      el.style.marginTop = `${Math.max(32, Math.round(window.innerHeight - RELATED_PEEK_PX - top))}px`;
    };
    place();
    window.addEventListener("resize", place);
    const ro = new ResizeObserver(place);
    if (el.previousElementSibling) ro.observe(el.previousElementSibling);
    return () => { window.removeEventListener("resize", place); ro.disconnect(); };
  }, [relatedItems.length]);
  const [fullscreenOpen, setFullscreenOpen] = useState(false);
  const [fsZoomed, setFsZoomed] = useState(false);
  const [fsPos, setFsPos] = useState({ x: 50, y: 50 });
  // Share falls back to copying the link where there is no share sheet (most
  // desktop browsers), instead of doing nothing; this is the note it shows.
  const [shareNote, setShareNote] = useState("");
  const shareNoteTimer = useRef(null);
  useEffect(() => () => clearTimeout(shareNoteTimer.current), []);

  useEffect(() => {
    // Scroll to top when navigating to a new product
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [itemId]);

  useEffect(() => {
    if (!fullscreenOpen) return;
    // utils/scrollLock rather than body overflow alone, which iOS Safari
    // ignores: the page behind the viewer kept scrolling under a swipe.
    lockScroll();
    const onKey = (e) => {
      if (e.key === "Escape") setFullscreenOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      unlockScroll();
      window.removeEventListener("keydown", onKey);
    };
  }, [fullscreenOpen]);

  useEffect(() => {
    if (!fullscreenOpen) {
      setFsZoomed(false);
      setFsPos({ x: 50, y: 50 });
    }
  }, [fullscreenOpen, activeIndex]);

  // ── Photo strip (tablet width and below): every photo is a full-width
  // slide the visitor swipes through, and the thumbnails follow. Desktop
  // keeps the single main image with thumbnails beside it. ──
  const isStripGallery = useMediaQuery(TABLET_QUERY);
  const stripRef = useRef(null);
  const handleStripScroll = useCallback(() => {
    const el = stripRef.current;
    if (!el) return;
    const i = Math.round(el.scrollLeft / Math.max(1, el.clientWidth));
    setActiveIndex((prev) => (prev === i ? prev : i));
  }, []);
  // A thumbnail picks the photo; on the strip it also slides to it.
  const showImage = useCallback((i) => {
    setActiveIndex(i);
    const el = stripRef.current;
    if (el) el.scrollTo({ left: i * el.clientWidth, behavior: "smooth" });
  }, []);
  // A new product starts the strip on its first photo.
  useEffect(() => {
    if (stripRef.current) stripRef.current.scrollLeft = 0;
  }, [itemId]);
  // Closing the viewer, where the photo may have changed, lines the strip up
  // with the photo that was last on screen.
  useEffect(() => {
    if (fullscreenOpen) return;
    const el = stripRef.current;
    if (el) el.scrollLeft = activeIndex * el.clientWidth;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullscreenOpen]);

  // ── Fullscreen viewer on touch: a swipe changes the photo; zoomed in, the
  // photo follows the finger. onMouseMove handles the pointer case. ──
  const fsTouchRef = useRef(null);
  const fsDraggedRef = useRef(false);
  const handleFsTouchStart = useCallback((e) => {
    const t = e.touches[0];
    fsTouchRef.current = { x: t.clientX, y: t.clientY, pos: fsPos };
    fsDraggedRef.current = false;
  }, [fsPos]);
  const handleFsTouchMove = useCallback((e) => {
    const start = fsTouchRef.current;
    if (!start || !fsZoomed) return;
    const t = e.touches[0];
    const rect = e.currentTarget.getBoundingClientRect();
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    if (Math.abs(dx) + Math.abs(dy) > 4) fsDraggedRef.current = true;
    const k = 100 / (FS_ZOOM - 1);
    setFsPos({
      x: clampPct(start.pos.x - (dx / rect.width) * k),
      y: clampPct(start.pos.y - (dy / rect.height) * k),
    });
  }, [fsZoomed]);

  // Record page view (fire-and-forget, no error surfaced to user)
  useEffect(() => {
    if (!itemId) return;
    fetch(`/api/views/${itemId}`, { method: "POST" }).catch(() => {});
  }, [itemId]);

  // The brands the owner hid from the Showcase (site manager's per-brand
  // switch). The brand label below opens that brand's row there, so for these
  // it stays a plain label instead of sending the visitor to the wrong brand.
  const [hiddenBrands, setHiddenBrands] = useState(() => getShowcaseHiddenBrands());
  useEffect(() => {
    let active = true;
    fetchShowcaseHiddenBrands().then((hidden) => { if (active) setHiddenBrands(hidden); });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    async function loadItem() {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`/api/inventory/${itemId}`);
        if (!res.ok) {
          throw new Error(`Item ${itemId} not found`);
        }
        const data = await res.json();
        setItem(data);
        // Parse size from CSV format
        const sizeStr = data.size || "";
        const parsedSize = sizeStr.includes(",") ? sizeStr.split(",")[0].trim() : sizeStr;
        setSelectedSize(parsedSize || null);
        setActiveIndex(0);
      } catch (e) {
        setError(e.message || "Failed to load item");
      } finally {
        setLoading(false);
      }
    }
    if (itemId) {
      loadItem();
    }
  }, [itemId]);

  useEffect(() => {
    async function loadRelated() {
      try {
        const res = await fetch("/api/inventory/?state=LIVE&is_available=true&limit=8");
        if (res.ok) {
          const data = await res.json();
          const payload = Array.isArray(data) ? data : data.items || [];
          const filtered = payload.filter((i) => i.id !== itemId).slice(0, 4);
          setRelatedItems(filtered);
        }
      } catch (e) {
        console.error("Failed to load related items:", e);
      }
    }
    if (item) {
      loadRelated();
    }
  }, [item, itemId]);

  const images = useMemo(() => {
    if (!item || !item.photos_json) return [];
    return item.photos_json.map((photo) => photo.url).filter(Boolean);
  }, [item]);

  const activeImage = images[activeIndex] || images[0] || null;

  const priceFormatted = useMemo(() => {
    if (!item) return "Price unavailable";
    const usd = formatDisplayPrice(item.price_usd, "USD");
    if (usd) return usd;
    return formatDisplayPrice(item.price, item.currency || "USD") || "Price unavailable";
  }, [item]);

  // Parse size and color from CSV format: "34, Black" -> size="34", color="Black"
  const { parsedSize, parsedColor } = useMemo(() => {
    if (!item || !item.size) {
      return { parsedSize: null, parsedColor: null };
    }
    const sizeStr = String(item.size).trim();
    if (sizeStr.includes(",")) {
      const parts = sizeStr.split(",").map(s => s.trim());
      return {
        parsedSize: parts[0] || null,
        parsedColor: parts.slice(1).join(", ") || null, // Handle multiple commas
      };
    }
    // If no comma, treat entire string as size
    return { parsedSize: sizeStr, parsedColor: null };
  }, [item]);

  const metaLines = useMemo(() => {
    if (!item) return [];
    // Headers first, then the description (the owner, 2026-10-17: "give all
    // headers this fully capitalized look, put them all at the top, and
    // always next line after all these headers for the description"). A
    // header is any "Key: value" line, the site's own (BRAND, COLOR, the
    // composition as FABRIC) or one written in the description (Fabric:,
    // Condition: ...); each is set "KEY : VALUE" in caps, BRAND, COLOR and
    // FABRIC first and the rest in the order written. A key said twice shows
    // once: the description's own words win (its "Light blue" over the
    // listing's "Blue"), except BRAND, which the listing's field names. The
    // rest of the description follows on the next line, as written in
    // Discord, its case untouched (2026-10-02: "dont uppercase it").
    const KEY_ALIASES = { COLOUR: "COLOR", COMPO: "FABRIC", COMPOSITION: "FABRIC", MATERIAL: "FABRIC" };
    const KEY_ORDER = ["BRAND", "COLOR", "FABRIC"];
    const normKey = (key) => {
      const k = key.trim().replace(/\s+/g, " ").toUpperCase();
      return KEY_ALIASES[k] || k;
    };
    // "Key: value", the key a few words of letters; not a link ("https://").
    const HEADER_RE = /^\s*([A-Za-z][A-Za-z&/ ]{0,22}?)\s*:\s*(\S.*)$/;
    // The first value for a key stands. So the listing's BRAND goes in
    // first, then the description's lines, then the listing's COLOR and
    // composition, which only fill in what the description did not say.
    const headers = new Map();
    const addHeader = (key, value) => {
      const k = normKey(key);
      const v = String(value).trim();
      if (v && !headers.has(k)) headers.set(k, v);
    };
    const brand = item.brand_name || item.brand;
    if (brand) addHeader("BRAND", brand);
    const body = [];
    String(item.description || "").split(/\r?\n/).forEach((line) => {
      const m = line.match(HEADER_RE);
      if (m && !m[2].startsWith("//") && m[1].trim().split(/\s+/).length <= 3) {
        addHeader(m[1], m[2]);
      } else {
        body.push(line);
      }
    });
    if (parsedColor) addHeader("COLOR", parsedColor);
    if (item.composition || item.material) addHeader("FABRIC", item.composition || item.material);
    // The description's blank lines at its ends go; inside it they stay.
    while (body.length && !body[0].trim()) body.shift();
    while (body.length && !body[body.length - 1].trim()) body.pop();
    const ordered = [
      ...KEY_ORDER.filter((k) => headers.has(k)),
      ...[...headers.keys()].filter((k) => !KEY_ORDER.includes(k)),
    ];
    // An empty line between the headers and the description (the owner,
    // 2026-10-17: "remember next line at the description"): a non-breaking
    // space, since an empty <div> has no height. Blank lines inside the
    // description stay as they always rendered, empty and collapsed.
    return [
      ...ordered.map((k) => ({ text: `${k} : ${headers.get(k).toUpperCase()}`, label: true })),
      ...(ordered.length && body.length ? [{ text: " ", label: false }] : []),
      ...body.map((line) => ({ text: line, label: false })),
    ];
  }, [item, parsedColor]);

  // The brand already sits above the title as the eyebrow, and again as the
  // BRAND line in the meta below it, so a title that opens with the brand says
  // the name three times — and at 32px it wrapped to three lines, pushing the
  // price down the page. This cuts that one leading repeat:
  // "ENFANTS RICHES DEPRIMES PÉ26 SMALL SNAKE BELT" shows as "PÉ26 SMALL SNAKE
  // BELT". The eyebrow and the meta line are untouched — the brand is still
  // said, just not three times.
  //
  // Matched with case and punctuation folded away, because titles are typed by
  // hand and "Enfants Riches Déprimés - PÉ26 …" is the same repeat. Accents are
  // deliberately NOT folded: they are left on both sides, so they still match
  // each other. Tokens rather than a string prefix, so a brand written "Y-3"
  // matches a title that writes it the same way. Only a whole-token prefix is
  // cut, and only when something survives it — a title that IS just the brand
  // keeps it rather than rendering empty.
  const displayTitle = useMemo(() => {
    const title = String(item?.title || "").trim();
    const brand = String(item?.brand_name || item?.brand || "").trim();
    if (!title) return "Untitled";
    if (!brand) return title;
    const norm = (v) => v.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, " ").trim();
    const brandTokens = norm(brand).split(" ").filter(Boolean);
    if (!brandTokens.length) return title;
    const words = title.split(/\s+/);
    const seen = [];
    let cut = -1;
    for (let w = 0; w < words.length && seen.length < brandTokens.length; w += 1) {
      seen.push(...norm(words[w]).split(" ").filter(Boolean));
      if (seen.length >= brandTokens.length) {
        cut = w + 1;
        break;
      }
    }
    if (cut < 0 || seen.join(" ") !== brandTokens.join(" ")) return title;
    // Whatever separated the brand from the rest goes with it.
    const rest = words.slice(cut).join(" ").replace(/^[\s\-–—:,/|]+/, "").trim();
    return rest || title;
  }, [item]);

  const sizeOptions = useMemo(() => {
    if (!item) return [];
    // Only show size values (before comma) in dropdown
    if (parsedSize) {
      return [parsedSize];
    }
    return ["One Size"];
  }, [item, parsedSize]);

  const isSoldOut = useMemo(() => {
    if (!item) return false;
    return item.sold || item.state !== "LIVE" || !item.is_available;
  }, [item]);

  // Showcase items are display-only: no Add to Cart, no Buy Now, no Klarna.
  const isShowcase = !!item?.showcase;
  const brandHidden = !!hiddenBrands?.has(String(item?.brand_name || item?.brand || "").toLowerCase().trim());

  const isAlreadyInCart = useMemo(() => {
    if (!item) return false;
    return isInCart(item.id, selectedSize || sizeOptions[0] || null, parsedColor || null);
  }, [item, selectedSize, sizeOptions, parsedColor, isInCart]);

  const handleAddToCart = async () => {
    if (!item || isSoldOut || isShowcase) return;
    
    // If already in cart, remove it
    if (isAlreadyInCart) {
      removeItem(item.id, selectedSize || sizeOptions[0] || null, parsedColor || null);
      return;
    }
    
    // Otherwise, add it
    setAddingToCart(true);
    try {
      const success = addItem(item, selectedSize || sizeOptions[0], parsedColor);
      if (success) {
        setAddedToCart(true);
        setTimeout(() => {
          setAddingToCart(false);
          setAddedToCart(false);
        }, 2000);
      } else {
        setAddingToCart(false);
      }
    } catch (e) {
      console.error("Failed to add to cart:", e);
      setAddingToCart(false);
    }
  };

  const handleShare = async () => {
    if (!item) return;
    const url = window.location.href;
    // Phones (and some desktops) have a native share sheet.
    if (navigator.share) {
      navigator
        .share({
          title: item.title,
          text: item.description || "",
          url,
        })
        .catch(() => {});
      return;
    }
    // Elsewhere the button used to do nothing at all; copy the link instead.
    let note = "Link copied";
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      note = "Copy failed";
    }
    setShareNote(note);
    clearTimeout(shareNoteTimer.current);
    shareNoteTimer.current = setTimeout(() => setShareNote(""), 2000);
  };

  const handleBuyNow = async () => {
    if (!item || isShowcase) return;
    if (isSoldOut) {
      alert("This item is no longer available");
      return;
    }
    try {
      const checkResponse = await fetch(`/api/inventory/${item.id}`);
      if (!checkResponse.ok) {
        alert("This item is no longer available");
        return;
      }
      const currentProduct = await checkResponse.json();
      const unavailable =
        currentProduct.sold || currentProduct.state !== "LIVE" || !currentProduct.is_available;
      if (unavailable) {
        alert("Sorry, this item was just sold!");
        window.location.reload();
        return;
      }
      // Navigate directly to checkout without adding to cart
      navigate(`/featured/checkout?item_id=${item.id}`);
    } catch (error) {
      console.error("Error:", error);
      alert("Something went wrong. Please try again.");
    }
  };

  // Klarna installments tap: drop the item in the cart (if not already there)
  // and send the buyer to the cart so they can review before checkout.
  const handleKlarnaInstallments = () => {
    if (!item || isSoldOut || isShowcase) return;
    if (!isAlreadyInCart) {
      addItem(item, selectedSize || sizeOptions[0], parsedColor);
    }
    navigate("/featured/cart");
  };


  const backButton = backTo ? (
    <button
      type="button"
      className="product-back"
      aria-label={backTo.startsWith("/showcase") || backTo.startsWith("/brand") ? "Back to showcase" : "Back to catalog"}
      onClick={handleBack}
    >
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
        <polyline points="15 18 9 12 15 6" />
      </svg>
      {/* Revealed on hover as the arrow steps left (app.css .product-back__label). */}
      <span className="product-back__label" aria-hidden="true">
        {backTo.startsWith("/showcase") || backTo.startsWith("/brand") ? "Back to showcase" : backTo === "/" ? "Back to home" : "Back to catalog"}
      </span>
    </button>
  ) : null;

  if (loading) {
    return (
      <div className="product-page">
        <div className="product-wrap">
          {backButton}
          <div className="product-loading">Loading...</div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !item) {
    return (
      <div className="product-page">
        <div className="product-wrap">
          {backButton}
          <div className="product-error">{error || "Product not found"}</div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="product-page">
      <div className="product-wrap">
        {backButton}
        <div className="product-grid">
          <div className="gallery-container">
            {images.length > 1 && (
              <div className="gallery-thumbs">
                {images.map((src, i) => (
                  <div key={src} className={`thumb ${i === activeIndex ? "is-active" : ""}`} onClick={() => showImage(i)}>
                    <img src={src} alt={`${item.title || ""} ${i + 1}`} />
                  </div>
                ))}
              </div>
            )}
            {isStripGallery && images.length > 1 ? (
              // Tablet width and below: swipe through the photos. Each slide
              // opens the fullscreen viewer on itself.
              <div
                className="gallery-main gallery-main--strip"
                ref={stripRef}
                onScroll={handleStripScroll}
                role="group"
                aria-label={`${item.title || "Product"} photos, ${images.length} in all`}
              >
                {images.map((src, i) => (
                  <div
                    key={src}
                    className="gallery-main__slide"
                    onClick={() => { setActiveIndex(i); setFullscreenOpen(true); }}
                  >
                    <img
                      src={src}
                      alt={`${item.title || ""} ${i + 1}`}
                      loading={i === 0 ? "eager" : "lazy"}
                      draggable={false}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div
                className="gallery-main"
                onClick={() => activeImage && setFullscreenOpen(true)}
              >
                {activeImage ? (
                  <img src={activeImage} alt={item.title || ""} />
                ) : (
                  <div className="gallery-placeholder">No image</div>
                )}
              </div>
            )}
          </div>

          <div className="product-info">
            {(item.brand_name || item.brand) && (
              <button
                type="button"
                className={`product-brand${brandHidden ? " product-brand--static" : ""}`}
                tabIndex={brandHidden ? -1 : undefined}
                aria-disabled={brandHidden || undefined}
                onClick={() => {
                  if (brandHidden) return;
                  navigate("/showcase", {
                    state: { targetBrand: item.brand_name || item.brand, hideHeader: true },
                  });
                }}
              >
                {item.brand_name || item.brand}
              </button>
            )}
            {/* Title through BUY NOW: on phones and tablets this block is the
                title's width, and the buttons span exactly that (the owner,
                2026-10-25: "only span the item title ... so its all synced"). */}
            <div className="product-buy">
            <h1 className="product-title">{displayTitle}</h1>

            {/* SIZE first, then the price (the owner, 2026-10-02: "put size above
                price"). */}
            {sizeOptions.length > 0 && (
              // Every piece here is a one-of-one — sizeOptions is always the
              // single parsed size, or "One Size" — so this is a statement, not
              // a choice. The <select> it replaces offered a decision that did
              // not exist, and was the only native form control on the page, so
              // it wore the browser's own styling in a page that has none.
              // Nothing downstream changes: the cart already read
              // selectedSize || sizeOptions[0], which is what the select's
              // value was, and selectedSize is still set when the item loads.
              <p className="product-size">
                <span className="product-size__label">Size:</span>{" "}
                <span className="product-size__value">{selectedSize || sizeOptions[0]}</span>
              </p>
            )}

            {/* Price Display with Optional Strikethrough */}
            <div className="product-price-container">
              {item.price_adjusted && item.original_price_usd ? (
                <>
                  <span className="product-price-original">
                    {formatDisplayPrice(item.original_price_usd, "USD")}
                  </span>
                  <span className="product-price">{priceFormatted}</span>
                </>
              ) : (
                <span className="product-price">{priceFormatted}</span>
              )}
            </div>

            {!isShowcase && (
              // In the cart it stays white, ALREADY IN CART, and turns red as
              // REMOVE FROM CART under the pointer; a press takes it out
              // (handleAddToCart). A touchscreen, with no hover, reads REMOVE
              // FROM CART outright (the owner, 2026-10-17).
              <button
                className={`add-to-cart${addedToCart ? " add-to-cart--added" : ""}${isAlreadyInCart ? " add-to-cart--in-cart" : ""}`}
                onClick={handleAddToCart}
                disabled={addingToCart || isSoldOut}
                aria-label={isAlreadyInCart && !addingToCart ? "Remove from cart" : undefined}
              >
                {addingToCart
                  ? "Adding..."
                  : isAlreadyInCart
                    ? (
                      <>
                        <span className="add-to-cart__in">Already in cart</span>
                        <span className="add-to-cart__remove">Remove from cart</span>
                      </>
                    )
                    : addedToCart
                      ? "Added to Cart"
                      : isSoldOut
                        ? "Sold Out"
                        : "Add to Cart"}
              </button>
            )}
            {!isShowcase && (
              <div className="buy-now-section">
                <button className="btn-buy-now" onClick={handleBuyNow} disabled={isSoldOut}>
                  {isSoldOut ? "SOLD OUT" : "BUY NOW"}
                </button>
                {isSoldOut && <p className="sold-message">This item has been sold</p>}
              </div>
            )}
            </div>
            {(() => {
              if (isShowcase) return null;
              const klarnaPrice = typeof item?.price_usd === "number"
                ? item.price_usd
                : (item?.currency === "USD" && typeof item?.price === "number" ? item.price : null);
              if (!klarnaPrice || klarnaPrice <= 0) return null;
              return (
                <div className="klarna-installments">
                  <span
                    className="klarna-installments__text"
                    role="button"
                    tabIndex={0}
                    onClick={handleKlarnaInstallments}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleKlarnaInstallments();
                      }
                    }}
                  >
                    4 interest-free payments of{" "}
                    <strong>{formatCurrency(klarnaPrice / 4, "USD")}</strong> with
                  </span>
                  <img src={klarnaBadge} alt="Klarna" className="klarna-installments__badge" />
                </div>
              );
            })()}

            {metaLines.length > 0 && (
              <div className="meta">
                {metaLines.map((line, i) => (
                  <div key={i} className={line.label ? "meta__label" : undefined}>{line.text}</div>
                ))}
              </div>
            )}

            <div className="social-row">
              <button className="social-btn" onClick={handleShare} aria-live="polite">
                {shareNote || "Share"}
              </button>
              <button
                className="social-btn"
                onClick={() =>
                  window.open("https://www.instagram.com/itrapswag/", "_blank")
                }
              >
                More Images
              </button>
            </div>
          </div>
        </div>

        {relatedItems.length > 0 && (
          <div className="related" ref={relatedRef}>
            <div className="related-title">You may also like</div>
            <div className="related-grid">
              {relatedItems.map((relatedItem, index) => (
                // The catalog's own card (size, sold state, cart plus), where
                // this row used to carry a stripped-down copy. Carries the
                // origin forward so the back arrow still returns to the
                // catalog / brand page after a related-item hop; the itemId
                // effect above scrolls the new product to the top.
                <ProductCard
                  key={relatedItem.id}
                  item={relatedItem}
                  index={index}
                  to={`/featured/item/${relatedItem.id}`}
                  state={backTo ? { from: backTo } : undefined}
                />
              ))}
            </div>
          </div>
        )}
      </div>
      <Footer />
      {fullscreenOpen && activeImage && (
        <div
          className="product-fullscreen"
          role="dialog"
          aria-modal="true"
          onClick={() => setFullscreenOpen(false)}
        >
          {images.length > 1 && (
            <div
              className="product-fullscreen__thumbs"
              onClick={(e) => e.stopPropagation()}
            >
              {images.map((src, i) => (
                <button
                  key={src}
                  type="button"
                  className={`product-fullscreen__thumb${i === activeIndex ? " product-fullscreen__thumb--active" : ""}`}
                  onClick={() => {
                    setActiveIndex(i);
                    setFsZoomed(false);
                    setFsPos({ x: 50, y: 50 });
                  }}
                  aria-label={`View image ${i + 1}`}
                >
                  <img src={src} alt={`${item.title || ""} ${i + 1}`} />
                </button>
              ))}
            </div>
          )}
          <button
            type="button"
            className="product-fullscreen__close"
            aria-label="Close fullscreen"
            onClick={(e) => {
              e.stopPropagation();
              setFullscreenOpen(false);
            }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
          <div
            className={`product-fullscreen__canvas${fsZoomed ? " product-fullscreen__canvas--zoomed" : ""}`}
            onTouchStart={handleFsTouchStart}
            onTouchMove={handleFsTouchMove}
            onTouchEnd={(e) => {
              const start = fsTouchRef.current;
              fsTouchRef.current = null;
              // A sideways swipe on the unzoomed photo moves to the next or
              // previous one, round the set.
              if (!start || fsZoomed || images.length < 2) return;
              const t = e.changedTouches[0];
              const dx = t.clientX - start.x;
              const dy = t.clientY - start.y;
              if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) {
                const step = dx < 0 ? 1 : -1;
                setActiveIndex((i) => (i + step + images.length) % images.length);
              }
            }}
            onClick={(e) => {
              e.stopPropagation();
              // The tap that ends a zoomed drag is not a request to zoom out.
              if (fsDraggedRef.current) {
                fsDraggedRef.current = false;
                return;
              }
              const rect = e.currentTarget.getBoundingClientRect();
              const x = ((e.clientX - rect.left) / rect.width) * 100;
              const y = ((e.clientY - rect.top) / rect.height) * 100;
              setFsPos({ x, y });
              setFsZoomed((z) => !z);
            }}
            onMouseMove={(e) => {
              if (!fsZoomed) return;
              const rect = e.currentTarget.getBoundingClientRect();
              const x = ((e.clientX - rect.left) / rect.width) * 100;
              const y = ((e.clientY - rect.top) / rect.height) * 100;
              setFsPos({ x, y });
            }}
            style={{ "--zoom-x": `${fsPos.x}%`, "--zoom-y": `${fsPos.y}%` }}
          >
            <img src={activeImage} alt={item.title || ""} />
          </div>
        </div>
      )}
    </div>
  );
}
